// src/components/dfs/ResearchTab.tsx
// Research tab — full player pool with season-to-date stats, virtualized.
// Columns: Name | GL | SH | SoT | SC | GC | IP | IC | DIS | Add

import React, { useEffect, useState, useMemo, useRef, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Lock, CircleCheck } from 'lucide-react';
import { ApiPlayer, fetchAllCompetitions } from '@/api/dfs';
import { PlayerStatsEntryForList } from '@/api/schemas';
import { statsLeaderboardQueryOptions } from '@/hooks/useDfsResearchQueries';
import { formatPlayerDisplayName, foldForSearch } from '@/utils/formatting';
import HorizontalPillFilter from '@/components/common/HorizontalPillFilter';
import PlayerProfileModal from '@/components/common/PlayerProfileModal';
import Spinner from '@/components/common/Spinner';
import AvailabilityBadge from '@/components/common/AvailabilityBadge';
import InjuryBadge from '@/components/common/InjuryBadge';
import { userFacingError } from '@/utils/userFacingError';

type PositionFilter = 'ALL' | 'FW' | 'MF' | 'DF' | 'GK';

type SortCol =
    | 'name'
    | 'goals' | 'shots' | 'shots_on_target'
    | 'sca' | 'gca'
    | 'prog_passes' | 'prog_carries'
    | 'dispossessed';

type SortDir = 'asc' | 'desc';

interface ResearchTabProps {
    contestId: number;
    competitionId: number;
    rankCompetitionIds?: number[];
    token: string | null;
    availablePlayers: ApiPlayer[];
    onAddPlayer: (player: ApiPlayer) => void;
    lineupPlayerIds?: Set<number>;
    lockedPlayerIds: Set<number>;
    remainingSalary?: number;
    positionFilter: PositionFilter;
    onPositionFilterChange: (f: PositionFilter) => void;
    searchTerm: string;
    onSearchTermChange: (t: string) => void;
    leagueShortNameForOprkContext?: string | null;
    sortConfig: { key: string; direction: 'asc' | 'desc' };
    onSortConfigChange: (config: { key: string; direction: 'asc' | 'desc' }) => void;
    isPoolLoading?: boolean;
    hideSearch?: boolean;
}

const fmt0 = (v: number | null | undefined) =>
    v == null || isNaN(v as number) ? '-' : String(Math.round(v as number));

const fmtSalary = (v: number | null | undefined) =>
    v == null ? '' : `$${(v / 1000).toFixed(1)}K`;

// Name | GL | SH | SoT | SC | GC | IP | IC | DIS | Add
const GRID = 'grid-cols-[minmax(0,2fr)_52px_52px_56px_52px_52px_52px_52px_56px_76px]';

function SortBtn({
    label, colKey, sortCol, sortDir, onClick, align = 'right',
}: {
    label: string; colKey: SortCol; sortCol: SortCol; sortDir: SortDir;
    onClick: (c: SortCol) => void; align?: 'left' | 'right' | 'center';
}) {
    const active = sortCol === colKey;
    const arrow = active ? (sortDir === 'asc' ? '↑' : '↓') : '↕';
    const justify = align === 'left' ? 'justify-start' : align === 'center' ? 'justify-center' : 'justify-end';
    return (
        <button
            type="button"
            className={`flex items-center gap-0.5 ${justify} hover:text-white active:text-white transition-colors w-full ${active ? 'text-accent-yellow' : ''}`}
            onClick={() => onClick(colKey)}
        >
            {align !== 'right' && <span>{label}</span>}
            <span className="text-micro opacity-50">{arrow}</span>
            {align === 'right' && <span>{label}</span>}
        </button>
    );
}

const ResearchTab: React.FC<ResearchTabProps> = ({
    contestId,
    rankCompetitionIds,
    competitionId,
    token,
    availablePlayers,
    onAddPlayer,
    lineupPlayerIds = new Set(),
    lockedPlayerIds,
    remainingSalary,
    positionFilter,
    onPositionFilterChange,
    searchTerm,
    onSearchTermChange,
    leagueShortNameForOprkContext,
    sortConfig,
    onSortConfigChange,
    isPoolLoading,
    hideSearch,
}) => {
    // Cached leaderboard query (shared definition with the page-level
    // prefetch). Keyed on the competition id, so a late-arriving id simply
    // fires the query then - the old effect fetched once with a hardcoded
    // EPL fallback and never re-fired, and re-fetched from scratch per mount.
    const {
        data: playerStats = [] as PlayerStatsEntryForList[],
        isPending: isLoadingMeta,
        error: statsQueryError,
    } = useQuery(statsLeaderboardQueryOptions(competitionId, token));
    const metaError = statsQueryError ? userFacingError(statsQueryError, 'Failed to load stats data.') : null;
    const scrollRef = useRef<HTMLDivElement>(null);

    // Derive sort from parent config
    const sortCol = (sortConfig.key || 'goals') as SortCol;
    const sortDir = (sortConfig.direction || 'desc') as SortDir;

    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
    const [selectedPlayerForModal, setSelectedPlayerForModal] = useState<ApiPlayer | null>(null);
    const [modalCompInfo, setModalCompInfo] = useState<{ id: number; name: string } | null>(null);

    // Modal competition context follows the contest's actual competition (EPL or MLS).
    // Falls back to fetching the competition list only if the parent didn't pass an id.
    useEffect(() => {
        if (competitionId) {
            setModalCompInfo({ id: competitionId, name: leagueShortNameForOprkContext || '' });
            return;
        }
        fetchAllCompetitions(true)
            .then(comps => {
                const fallback = comps[0];
                if (fallback) setModalCompInfo({ id: fallback.id, name: fallback.name });
            })
            .catch(() => {});
    }, [competitionId, leagueShortNameForOprkContext]);

    const handlePlayerRowClick = (player: ApiPlayer) => {
        setSelectedPlayerForModal(player);
        setIsProfileModalOpen(true);
    };

    const statsMap = useMemo(() => {
        const m = new Map<number, PlayerStatsEntryForList>();
        playerStats.forEach(s => {
            const key = (s as any).id ?? s.player_id;
            if (key != null) m.set(key, s);
        });
        return m;
    }, [playerStats]);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
    }, [sortCol, sortDir]);

    const handleSort = (col: SortCol) => {
        if (col === sortCol) {
            onSortConfigChange({ key: col, direction: sortDir === 'asc' ? 'desc' : 'asc' });
        } else {
            onSortConfigChange({ key: col, direction: col === 'name' ? 'asc' : 'desc' });
        }
    };

    // Client-side filter + sort over the full pool
    const rows = useMemo(() => {
        return availablePlayers
            .filter(dfs => {
                if (positionFilter !== 'ALL' && dfs.primary_position !== positionFilter) return false;
                if (searchTerm) {
                    const name = foldForSearch(formatPlayerDisplayName(dfs));
                    if (!name.includes(foldForSearch(searchTerm))) return false;
                }
                return true;
            })
            .map(dfs => ({ dfs, stat: statsMap.get(dfs.id) }))
            .sort((a, b) => {
                const dir = sortDir === 'asc' ? 1 : -1;
                if (sortCol === 'name') {
                    return dir * formatPlayerDisplayName(a.dfs).localeCompare(formatPlayerDisplayName(b.dfs));
                }
                const val = (row: typeof a): number => {
                    switch (sortCol) {
                        case 'goals':           return row.stat?.goals ?? -1;
                        case 'shots':           return row.stat?.shots ?? -1;
                        case 'shots_on_target': return row.stat?.shots_on_target ?? -1;
                        case 'sca':             return row.stat?.sca ?? -1;
                        case 'gca':             return row.stat?.gca ?? -1;
                        case 'prog_passes':     return row.stat?.prog_passes ?? -1;
                        case 'prog_carries':    return row.stat?.prog_carries ?? -1;
                        case 'dispossessed':    return row.stat?.dispossessed ?? -1;
                        default:                return 0;
                    }
                };
                return dir * (val(a) - val(b));
            });
    }, [availablePlayers, statsMap, sortCol, sortDir, positionFilter, searchTerm]);

    // Virtualization
    const rowVirtualizer = useVirtualizer({
        count: rows.length,
        getScrollElement: () => scrollRef.current,
        estimateSize: () => 40,
        overscan: 6,
    });

    const filterItems = [
        { id: 'ALL' as const, name: 'All' },
        { id: 'FW' as const, name: 'FW' },
        { id: 'MF' as const, name: 'MF' },
        { id: 'DF' as const, name: 'DF' },
        { id: 'GK' as const, name: 'GK' },
    ];

    const isLoading = isPoolLoading || (isLoadingMeta && availablePlayers.length === 0);

    return (
        <>
        <div className="flex flex-col flex-grow min-h-0">
            {/* Search (hidden on desktop when header search is visible) */}
            {!hideSearch && (
                <div className="mb-2">
                    <input
                        type="text"
                        placeholder="Search Players..."
                        value={searchTerm}
                        onChange={e => onSearchTermChange(e.target.value)}
                        className="w-full p-3 rounded-lg bg-gray-900 text-white border-2 border-gray-700 focus:border-accent-yellow focus:outline-none placeholder-gray-500 text-base font-medium"
                    />
                </div>
            )}

            {/* Position filter */}
            <div className="mb-3">
                <HorizontalPillFilter
                    items={filterItems}
                    selectedId={positionFilter}
                    onSelect={id => onPositionFilterChange(id as PositionFilter)}
                />
            </div>

            {/* Legend */}
            <div className="mb-2 px-1 text-badge text-gray-500 flex gap-3 flex-wrap">
                <span><span className="text-accent-yellow font-bold">GL</span> goals</span>
                <span><span className="text-accent-yellow font-bold">SH</span> shots</span>
                <span><span className="text-accent-yellow font-bold">SoT</span> shots on target</span>
                <span><span className="text-accent-yellow font-bold">SC</span> shot-creating actions</span>
                <span><span className="text-accent-yellow font-bold">GC</span> goal-creating actions</span>
                <span><span className="text-accent-yellow font-bold">IP</span> prog. passes</span>
                <span><span className="text-accent-yellow font-bold">IC</span> prog. carries</span>
                <span><span className="text-accent-yellow font-bold">DIS</span> dispossessed</span>
            </div>

            {/* Table */}
            <div
                className="overflow-y-auto flex-grow border-t border-gray-700"
                ref={scrollRef}
            >
                {isLoading ? (
                    <div className="flex items-center justify-center h-32">
                        <Spinner size="md" color="text-accent-yellow" />
                    </div>
                ) : metaError ? (
                    <div className="p-4 text-center text-secondary text-sm">{metaError}</div>
                ) : (
                    <>
                        {/* Sticky header */}
                        <div className="sticky top-0 z-10 bg-primary/95 backdrop-blur">
                            <div className={`grid items-center px-3 py-2 border-b border-gray-700 text-badge uppercase tracking-wide text-info-blue font-heading font-bold ${GRID}`}>
                                <SortBtn label="Name"  colKey="name"           sortCol={sortCol} sortDir={sortDir} onClick={handleSort} align="left" />
                                <SortBtn label="GL"    colKey="goals"          sortCol={sortCol} sortDir={sortDir} onClick={handleSort} />
                                <SortBtn label="SH"    colKey="shots"          sortCol={sortCol} sortDir={sortDir} onClick={handleSort} />
                                <SortBtn label="SoT"   colKey="shots_on_target" sortCol={sortCol} sortDir={sortDir} onClick={handleSort} />
                                <SortBtn label="SC"    colKey="sca"            sortCol={sortCol} sortDir={sortDir} onClick={handleSort} />
                                <SortBtn label="GC"    colKey="gca"            sortCol={sortCol} sortDir={sortDir} onClick={handleSort} />
                                <SortBtn label="IP"    colKey="prog_passes"    sortCol={sortCol} sortDir={sortDir} onClick={handleSort} />
                                <SortBtn label="IC"    colKey="prog_carries"   sortCol={sortCol} sortDir={sortDir} onClick={handleSort} />
                                <SortBtn label="DIS"   colKey="dispossessed"   sortCol={sortCol} sortDir={sortDir} onClick={handleSort} />
                                <div className="text-center text-badge">Add</div>
                            </div>
                        </div>

                        {rows.length === 0 ? (
                            <div className="p-4 text-center text-gray-500 text-sm italic">No players match your filters.</div>
                        ) : (
                            <div style={{ height: `${rowVirtualizer.getTotalSize()}px`, position: 'relative' }}>
                                {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                                    const { dfs, stat } = rows[virtualRow.index];
                                    const inLineup      = lineupPlayerIds.has(dfs.id);
                                    const isLocked       = lockedPlayerIds.has(dfs.id);
                                    const isSalaryValid = typeof dfs.dfs_salary === 'number' && dfs.dfs_salary >= 0;
                                    const isAffordable  = remainingSalary === undefined || !isSalaryValid || (dfs.dfs_salary ?? 0) <= remainingSalary;
                                    const notInSquad    = dfs.availability_status === 'NOT_IN_SQUAD';
                                    const isDisabled    = isLocked || inLineup || !isAffordable || !isSalaryValid || notInSquad;

                                    let btnText: ReactNode = '+ Add';
                                    if (isLocked)        btnText = <Lock size={14} />;
                                    else if (inLineup)   btnText = <CircleCheck size={14} />;
                                    else if (notInSquad) btnText = 'N/A';

                                    return (
                                        <div
                                            key={dfs.id}
                                            className="absolute top-0 left-0 w-full"
                                            style={{ height: `${virtualRow.size}px`, transform: `translateY(${virtualRow.start}px)` }}
                                        >
                                            <div
                                                className={`grid items-center px-3 h-full border-b border-gray-800 text-xs
                                                    ${inLineup ? 'bg-blue-900/20' : 'hover:bg-primary/60 active:bg-primary/60'}
                                                    ${isDisabled && !inLineup ? 'opacity-60' : ''}
                                                    ${GRID}`}
                                            >
                                                {/* Name */}
                                                <button
                                                    type="button"
                                                    className={`leading-tight pr-1 text-left w-full min-w-0 ${!isLocked ? 'group/name' : ''}`}
                                                    onClick={() => !isLocked && handlePlayerRowClick(dfs)}
                                                >
                                                    <div className={`font-medium text-gray-100 truncate ${!isLocked ? 'group-hover/name:text-accent-yellow group-hover/name:underline group-hover/name:underline-offset-2 group-active/name:text-accent-yellow group-active/name:underline group-active/name:underline-offset-2' : ''}`} title={formatPlayerDisplayName(dfs)}>
                                                        {formatPlayerDisplayName(dfs)}
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <span className="text-badge text-gray-500">{dfs.primary_position || ''}</span>
                                                        <AvailabilityBadge status={dfs.availability_status} isLocked={isLocked} />
                                                        <InjuryBadge status={dfs.injury_status} details={dfs.injury_details} />
                                                    </div>
                                                </button>

                                                <div className="text-right text-gray-200">{fmt0(stat?.goals)}</div>
                                                <div className="text-right text-gray-200">{fmt0(stat?.shots)}</div>
                                                <div className="text-right text-gray-200">{fmt0(stat?.shots_on_target)}</div>
                                                <div className="text-right text-gray-200">{fmt0(stat?.sca)}</div>
                                                <div className="text-right text-gray-200">{fmt0(stat?.gca)}</div>
                                                <div className="text-right text-gray-200">{fmt0(stat?.prog_passes)}</div>
                                                <div className="text-right text-gray-200">{fmt0(stat?.prog_carries)}</div>
                                                <div className="text-right text-gray-200">{fmt0(stat?.dispossessed)}</div>

                                                {/* Salary + Add */}
                                                <div className="flex flex-col items-center gap-0.5">
                                                    {isSalaryValid && (
                                                        <div className={`text-badge font-bold font-heading ${!isAffordable ? 'text-secondary' : 'text-accent-yellow'}`}>{fmtSalary(dfs.dfs_salary)}</div>
                                                    )}
                                                    <button
                                                        onClick={e => { e.stopPropagation(); if (!isDisabled) onAddPlayer(dfs); }}
                                                        disabled={isDisabled}
                                                        className={`px-2 py-1 rounded text-xs font-bold transition-colors
                                                            ${isDisabled
                                                                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                                                : 'bg-green-600 hover:bg-green-500 active:bg-green-500 text-white'}`}
                                                    >
                                                        {btnText}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>

        {selectedPlayerForModal && modalCompInfo && (
            <PlayerProfileModal
                isOpen={isProfileModalOpen}
                onClose={() => { setIsProfileModalOpen(false); setSelectedPlayerForModal(null); }}
                player={selectedPlayerForModal}
                competitionId={modalCompInfo.id}
                rankCompetitionIds={rankCompetitionIds}
                competitionName={modalCompInfo.name}
                dfsContestId={contestId}
            />
        )}
        </>
    );
};

export default ResearchTab;
